import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TechnicalDetails = ({ archiveData }) => {
  const [expandedSection, setExpandedSection] = useState('');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  const handleContractView = () => {
    window.open(`https://polygonscan.com/address/${archiveData?.contractAddress}`, '_blank');
  };

  const handleSourceCode = () => {
    window.open('https://github.com/polynotary/contracts', '_blank');
  };

  const technicalSections = [
    {
      id: 'blockchain',
      title: 'Blockchain Details',
      icon: 'Blocks',
      items: [
        { label: 'Network', value: 'Polygon Mainnet' },
        { label: 'Contract Address', value: archiveData?.contractAddress },
        { label: 'Block Confirmations', value: `${archiveData?.confirmations} confirmations` },
        { label: 'Gas Price', value: `${archiveData?.gasPrice} Gwei` },
        { label: 'Nonce', value: archiveData?.nonce }
      ]
    },
    {
      id: 'ipfs',
      title: 'IPFS Storage',
      icon: 'Database',
      items: [
        { label: 'Content Hash', value: archiveData?.contentHash },
        { label: 'Pin Status', value: 'Permanently Pinned' },
        { label: 'Gateway URLs', value: `${archiveData?.gatewayUrls?.length} available` },
        { label: 'Replication', value: 'Multi-node distributed' },
        { label: 'Content Type', value: 'image/png' }
      ]
    },
    {
      id: 'cryptographic',
      title: 'Cryptographic Proof',
      icon: 'Lock',
      items: [
        { label: 'Hash Algorithm', value: 'SHA-256' },
        { label: 'Signature Method', value: 'ECDSA' },
        { label: 'Merkle Root', value: archiveData?.merkleRoot },
        { label: 'Timestamp Proof', value: 'RFC 3161 Compliant' },
        { label: 'Integrity Check', value: 'Verified ✓' }
      ]
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Settings" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Technical Details</h3>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            iconPosition="left"
            onClick={handleContractView}
          >
            View Contract
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Github"
            onClick={handleSourceCode}
          />
        </div>
      </div>
      {/* Technical Sections */}
      <div className="space-y-4">
        {technicalSections?.map((section) => (
          <div key={section?.id} className="border border-border rounded-md">
            <button
              onClick={() => toggleSection(section?.id)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-smooth"
            >
              <div className="flex items-center space-x-3">
                <Icon name={section?.icon} size={16} className="text-primary" />
                <span className="font-medium text-foreground">{section?.title}</span>
              </div>
              <Icon 
                name={expandedSection === section?.id ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                className="text-muted-foreground" 
              />
            </button>

            {expandedSection === section?.id && (
              <div className="px-4 pb-4 border-t border-border">
                <div className="space-y-3 pt-4">
                  {section?.items?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-2">
                      <span className="text-sm text-muted-foreground">{item?.label}</span>
                      <span className="text-sm font-mono text-foreground max-w-xs truncate" title={item?.value}>
                        {item?.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Smart Contract Info */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-start space-x-3 p-4 bg-primary/5 border border-primary/20 rounded-md">
          <Icon name="Code" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-primary">Smart Contract Transparency</h4>
            <p className="text-xs text-primary/80 mt-1">
              All notarization logic is executed through verified smart contracts on Polygon. 
              The source code is publicly auditable and the contract state is immutable.
            </p>
            <div className="flex items-center space-x-4 mt-3">
              <div className="flex items-center space-x-1 text-xs text-primary">
                <Icon name="CheckCircle" size={12} />
                <span>Contract Verified</span>
              </div>
              <div className="flex items-center space-x-1 text-xs text-primary">
                <Icon name="Shield" size={12} />
                <span>Security Audited</span>
              </div>
              <div className="flex items-center space-x-1 text-xs text-primary">
                <Icon name="Globe" size={12} />
                <span>Open Source</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalDetails;